import { useState, useEffect } from 'react';
import { useConference } from '@/context/ConferenceContext';

const Countdown = () => {
  const { importantDetails, loading } = useConference();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (loading || !importantDetails?.ConferenceDates) return;

    
    // Parse the conference start date from the date string (e.g., "September 17-19, 2026" -> September 17, 2026)
    let targetDate;
    try {
      const dateString = importantDetails.ConferenceDates;
      
      // Handle both range format (17-19) and single date format (17)
      const rangeMatch = dateString.match(/^(\w+)\s+(\d+)-(\d+),\s+(\d+)$/);
      const singleMatch = dateString.match(/^(\w+)\s+(\d+),\s+(\d+)$/);
      
      let startDateString;
      
      if (rangeMatch) {
        // Range format: "September 17-19, 2026" -> "September 17, 2026"
        const [, month, startDay, , year] = rangeMatch;
        startDateString = `${month} ${startDay}, ${year}`;
      } else if (singleMatch) {
        // Single date format: "September 17, 2026"
        const [, month, startDay, year] = singleMatch;
        startDateString = `${month} ${startDay}, ${year}`;
      } else {
        // Fallback: try to parse as is
        startDateString = dateString;
      }
      
      targetDate = new Date(startDateString).getTime();
    } catch (error) {
      console.error('Error parsing conference start date:', error);
      return;
    }

    if (!targetDate || isNaN(targetDate)) return;

    const interval = setInterval(() => {
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000),
        });
      } else {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [importantDetails, loading]);

  if (loading) {
    return (
      <div className="flex justify-center gap-4 md:gap-8" data-testid="countdown-timer">
        {['days', 'hours', 'minutes', 'seconds'].map((unit) => (
          <div key={unit} className="text-center">
            <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 min-w-[80px] md:min-w-[100px]">
              <div className="w-8 h-8 md:w-12 md:h-12 bg-gray-200 rounded animate-pulse mx-auto"></div>
              <div className="text-xs md:text-sm text-gray-600 mt-2 capitalize">{unit}</div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex justify-center gap-4 md:gap-8" data-testid="countdown-timer">
      {Object.entries(timeLeft).map(([unit, value]) => (
        <div key={unit} className="text-center">
          <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 min-w-[80px] md:min-w-[100px]">
            <div className="text-3xl md:text-5xl font-bold text-sky-500" data-testid={`countdown-${unit}`}>
              {value.toString().padStart(2, '0')}
            </div>
            <div className="text-xs md:text-sm text-gray-600 mt-2 capitalize">{unit}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default Countdown;
